import React, { useEffect, useState } from 'react';
import TableFilters from './TableComponents/TableFilters';
import {
  RenderTableBody,
  RenderTableHeader,
} from './TableComponents/Components';
import styles from './Table.module.scss';
import Paginator from './TableComponents/TablePaginator/Paginator';
import { useFetch } from 'hooks/useFetch';
import LoadingSpinner from 'component/Custom/LoadingSpinner/LoadingSpinner';

const TableComponent = ({
  rowKey,
  headerData,
  externalData,
  actions = {
    hasEdit: {
      func: null,
    },
    onRowClick: {
      hasRowClick: false,
      func: null,
    },
  },
  filterButtons = [],
  rowButtons = [],
  renderAdditionalComponent,
  api,
  isRefetch,
  noPadding,
  handleExport,
  handleUploadCSV,
  filterData = [],
  filter,
  isFormResponse,
  isKnowledgeBase,
  exportApi,
  hasAction,
  tableContainerClassName = '',
  getData,
  hasFileModal = false,
  noDataText = 'No Data Found',
  noDataIcon = 'noData',
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [data, setData] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [hasIsLoading, setHasIsLoading] = useState(false);
  const [query, setQuery] = useState(
    !headerData?.hasPaging
      ? api?.get?.params ?? {}
      : {
          pageSize: 10,
          pageNumber: 1,
          ...(api?.get?.params || {}),
        }
  );

  useEffect(() => {
    if (api?.get?.params) {
      setQuery((prevQuery) => ({
        ...prevQuery,
        ...api.get.params,
      }));
    }
  }, [api?.get?.params]);

  const {
    data: urlData,
    refetch: refetchData,
    isFetching,
  } = useFetch(
    [api?.get?.queryKey, query],
    api?.get?.url,
    {
      enabled:
        api?.get?.enabled !== undefined
          ? api?.get?.enabled
          : api?.get?.url !== null && api?.get?.url !== undefined,
    },
    query,
    hasIsLoading ? api?.get?.refetchInterval : false
  );

  useEffect(() => {
    if (urlData?.data?.items?.some((item) => item.isLoading)) {
      setHasIsLoading(true);
    }
  }, [urlData]);

  useEffect(() => {
    if (externalData) {
      setSourceData(externalData || []);
    } else {
      if (urlData?.data?.items) {
        if (isFormResponse) {
          const mapped =
            urlData?.data?.items?.map((item) => {
              const formDetails = item.formDetails?.reduce((acc, i) => {
                acc[i.fieldLabel] = i.value;
                return acc;
              }, {});
              return {
                ...formDetails,
                createdOn: item.createdOn,
                chatId: item.chatId,
              };
            }) || [];
          setSourceData(mapped);
        } else {
          if (getData) {
            getData(urlData?.data);
          }
          setSourceData(urlData?.data?.items || []);
        }
      } else {
        setSourceData(urlData?.data || []);
      }
    }
  }, [externalData, urlData]);

  useEffect(() => {
    if (headerData?.hasInternalSearch) {
      const term = (internalSearchTerm || '').toLowerCase();
      if (!term) {
        setData(sourceData || []);
        return;
      }
      const fields = headerData?.fields || [];
      const filtered = (sourceData || []).filter((item) =>
        fields.some((f) => {
          const v = item?.[f.fieldName];
          if (v === null || v === undefined) return false;
          return String(v).toLowerCase().includes(term);
        })
      );
      setData(filtered);
    } else {
      setData(sourceData || []);
    }
  }, [internalSearchTerm, sourceData, headerData?.hasInternalSearch]);

  useEffect(() => {
    const fieldWithDesc = headerData?.fields.find((item) => item.isDesc);
    if (fieldWithDesc) {
      setQuery((prevParams) => ({
        ...prevParams,
        sortBy:
          fieldWithDesc.fieldName.charAt(0).toUpperCase() +
          fieldWithDesc.fieldName.slice(1),
        desc: false,
      }));
    }
  }, [headerData]);

  const totalPages = Math.ceil(
    urlData?.data?.totalCount / urlData?.data?.pageSize
  );
  const hasData = data?.length > 0 || urlData?.data?.totalCount;

  const handlePageChange = (page) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      pageNumber: page,
    }));
  };

  useEffect(() => {
    if (isRefetch !== null && isRefetch !== undefined) refetchData();
  }, [isRefetch]);

  useEffect(() => {
    setSelectedItems([]);
  }, [isRefetch]);

  return (
    <div
      className={`${styles.cont} ${
        noPadding ? styles.wider : ''
      } ${tableContainerClassName}`}
    >
      <TableFilters
        headerData={headerData}
        setQuery={setQuery}
        query={query}
        selectedItems={selectedItems}
        filterButtons={filterButtons}
        handleExport={handleExport}
        handleUploadCSV={handleUploadCSV}
        filterData={filterData}
        filter={filter}
        data={data}
        exportApi={exportApi}
        onInternalSearch={setInternalSearchTerm}
      />
      <div className={styles['table-wrapper']}>
        <div className={styles['table-scroll-container']}>
          <table className={styles['table-container']}>
            <RenderTableHeader
              headerData={headerData}
              setQuery={setQuery}
              query={query}
              rowKey={rowKey}
              rowButtons={rowButtons}
              renderAdditionalComponent={renderAdditionalComponent}
            />
            <RenderTableBody
              headerData={headerData}
              data={data}
              actions={actions}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              rowButtons={rowButtons}
              hasData={hasData}
              rowKey={rowKey}
              api={api}
              refetch={refetchData}
              isFetching={isFetching}
              isKnowledgeBase={isKnowledgeBase}
              renderAdditionalComponent={renderAdditionalComponent}
              hasAction={hasAction}
              hasFileModal={hasFileModal}
              noDataText={noDataText}
              noDataIcon={noDataIcon}
            />
          </table>
        </div>
      </div>
      {!isFetching &&
      headerData?.hasPaging &&
      urlData?.data?.totalCount > 0 &&
      data?.length > 0 &&
      totalPages !== 1 ? (
        <Paginator
          onPageChange={handlePageChange}
          currentPage={query.pageNumber}
          totalPages={totalPages}
        />
      ) : null}
    </div>
  );
};

export default TableComponent;
